import { datadogRum } from '@datadog/browser-rum';

import returnAppConfig from '@/helpers/return-app-config';
import monitoringAttributes from './monitoring-event-attributes';

const SERVICE_NAME = 'pay-at-table';

// Datadog Browser RUM runs alongside New Relic while we migrate our monitoring.
// It is a bundled SDK (no inline snippet), initialized once at plugin install.
// https://docs.datadoghq.com/real_user_monitoring/browser/setup/client/
const DataDogMonitoringService = (store) => {
  // Keep the current merchant/location on the RUM global context so every event
  // (sessions, views, errors, resources) is filterable by merchant, not just the
  // custom actions. Fires immediately, then again whenever the location changes.
  const syncGlobalContext = () => {
    store.watch(
      state => [
        state.merchant.internalId,
        state.locations?.selectedLocation.storeId,
        state.locations?.selectedLocation.description
      ],
      ([merchantId, storeId, storeName]) => {
        datadogRum.setGlobalContextProperty('merchantId', merchantId);
        datadogRum.setGlobalContextProperty('storeId', storeId);
        datadogRum.setGlobalContextProperty('storeName', storeName);
      },
      { immediate: true }
    );
  };

  const init = () => {
    const {
      datadogApplicationId,
      datadogClientToken,
      datadogSite,
      environment
    } = returnAppConfig();

    // Only initialize when credentials are present so local/test builds without
    // Datadog config are a no-op rather than reporting bad data.
    if (!datadogApplicationId || !datadogClientToken) {
      return;
    }

    datadogRum.init({
      applicationId: datadogApplicationId,
      clientToken: datadogClientToken,
      site: datadogSite || 'datadoghq.com',
      service: SERVICE_NAME,
      env: environment,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input'
    });

    syncGlobalContext();
  };

  const triggerDataDogEvent = async (eventName, severity, message, data = 'No data provided') => {
    const eventParams = { eventName, severity, message, eventData: data };

    const eventAttributes = await monitoringAttributes.getAttributes(eventParams, store);

    /** https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_events/#add-your-own-actions */
    datadogRum.addAction(eventName, eventAttributes);

    monitoringAttributes.triggerDebuggerLogging('Datadog', eventName, eventAttributes);
  };

  return {
    init,
    triggerDataDogEvent
  };
};

export default DataDogMonitoringService;
